import type { ReactNode, Ref } from "react";

/** The centered column every flow screen lays out in — its side gutters
 * only, for chrome that sits above a screen's <main>. */
export const MAIN_GUTTER_CLASS = "mx-auto w-full px-4 2xs:px-5 sm:px-10";
export const NARROW_MAIN_WIDTH_CLASS = "max-w-[760px]";

const MAIN_BASE = `${MAIN_GUTTER_CLASS} flex-1 pb-16 pt-7 sm:pt-11`;

/** The flow's narrow page container — the walk, its loading stand-ins,
 * and every non-wide juncture screen share it exactly, so a handoff
 * between them moves nothing. */
export const NARROW_MAIN_CLASS = `${MAIN_BASE} ${NARROW_MAIN_WIDTH_CLASS}`;

/** The heading's size on a phone; every size meets at 39px from `sm` up.
 * `compact` is for headings that run to a full question, as the quiz's
 * do, which the default size wraps to five or six lines on a phone. */
const TITLE_SIZE_CLASS: Record<"default" | "compact", string> = {
  default: "text-[31px] sm:text-[39px]",
  compact: "text-[27px] sm:text-[39px]",
};

/** The flow screens' shared stance: kicker, serif heading with a bold
 * tail, blurb — the mockup's interrupt-screen chrome. */
export default function ScreenChrome({
  kicker,
  titleA,
  titleB,
  blurb,
  wide,
  titleSize = "default",
  aside,
  titleRef,
  subheader,
  titlePlaceholder = false,
  children,
}: {
  /** Empty omits the kicker line. */
  kicker: string;
  titleA: string;
  titleB: string;
  blurb: string;
  /** Pricing-card faces span the quote screen's width; the rest narrow. */
  wide?: boolean;
  titleSize?: keyof typeof TITLE_SIZE_CLASS;
  /** Rendered beside the heading, bottom-aligned — the upsell's quiz
   * button lives here. */
  aside?: ReactNode;
  /** Makes the heading programmatically focusable, for moving focus to
   * it when the screen's content changes in place. */
  titleRef?: Ref<HTMLHeadingElement>;
  /** Content set in the blurb's place and spacing, under the heading, for a
   * screen whose first line is not a sentence of copy. It spans the screen's
   * full width, so what it holds can line up with the controls below. */
  subheader?: ReactNode;
  /** Holds the heading's line with a shimmering bar, for a screen still
   * waiting to learn its title. */
  titlePlaceholder?: boolean;
  children: ReactNode;
}) {
  return (
    <main className={wide ? `${MAIN_BASE} max-w-[1180px]` : NARROW_MAIN_CLASS}>
      <div className="flex flex-wrap items-end justify-between gap-5">
        <div className={subheader ? "w-full" : "max-w-[58ch]"}>
          {kicker !== "" && (
            <p className="text-[13px] font-bold uppercase tracking-[0.25em] text-evergreen">
              {kicker}
            </p>
          )}
          <h1
            ref={titleRef}
            tabIndex={titleRef ? -1 : undefined}
            className={`${kicker !== "" ? "mt-2.5" : ""} ${titlePlaceholder ? "relative" : ""} font-serif font-normal leading-[1.15] text-soil ${TITLE_SIZE_CLASS[titleSize]}`}
          >
            {titlePlaceholder ? (
              <>
                <span className="sr-only">Loading</span>
                <span aria-hidden className="invisible">
                  {titleA} <span className="font-sans font-bold">{titleB}</span>
                </span>
                <span
                  aria-hidden
                  className="absolute left-0 top-1/2 h-[0.7em] w-[9ch] -translate-y-1/2 animate-pulse rounded bg-oat-200 motion-reduce:animate-none"
                />
              </>
            ) : (
              <>
                {titleA}{" "}
                <span className="font-sans font-bold text-evergreen">
                  {titleB}
                </span>
              </>
            )}
          </h1>
          {blurb !== "" && (
            <p className="mt-3 max-w-[56ch] text-xl leading-[1.55] text-oat-700">
              {blurb}
            </p>
          )}
          {subheader && <div className="mt-3">{subheader}</div>}
        </div>
        {aside}
      </div>
      {children}
    </main>
  );
}
